
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useMemo } from "react";
import { 
  ArrowUpRight, 
  ArrowDownLeft, 
  Settings, 
  BarChart2, 
  RefreshCw, 
  Activity,
  Shield,
  Zap,
  TrendingUp,
  ShieldCheck,
  Repeat,
  ArrowRightLeft,
  ChevronRight,
  Key,
  History,
  Lock,
  ExternalLink
} from "lucide-react";
import { useWallet } from "../../hooks/useWallet";
import { ViewState } from "../../types";
import { Button } from "../ui/Button";
import { Card } from "../ui/Card";
import { AnimatePresence, motion } from 'framer-motion';
import { WalletTransactionList } from './wallet/WalletTransactionList';
import { SendModal } from './wallet/SendModal';
import { WalletSettingsModal } from "./wallet/WalletSettingsModal";
import { AssetRow } from './wallet/AssetRow';
import { DepositModal } from "./wallet/DepositModal";
import { TokenIcon } from "../ui/ChainIcons";

interface WalletViewProps {
  onNavigate: (view: ViewState) => void;
}

export const WalletView: React.FC<WalletViewProps> = ({ onNavigate }) => {
  const { walletAddress, balances, isConnected, refreshBalances, walletType } = useWallet();
  const [showDeposit, setShowDeposit] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMixer, setShowMixer] = useState(false);
  const [showSendSuccess, setShowSendSuccess] = useState(false);

  const { portfolioValue, portfolioChange, portfolioChangeClass } = useMemo(() => {
    const total = balances.reduce((acc, token) => acc + token.balance * token.priceUsd, 0);
    const changePercent = 4.25; 
    const changeValue = total * (changePercent / 100);
    return {
      portfolioValue: total,
      portfolioChange: {
          percent: changePercent.toFixed(2),
          value: changeValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
          isPositive: true,
      },
      portfolioChangeClass: 'text-emerald-600 dark:text-emerald-500'
    };
  }, [balances]);

  const activeAssets = useMemo(() => {
    return [...balances]
      .filter(b => b.balance >= 0)
      .sort((a, b) => (b.balance * b.priceUsd) - (a.balance * a.priceUsd));
  }, [balances]);

  const handleSendComplete = () => {
      setShowSend(false);
      setShowSendSuccess(true);
      setTimeout(() => setShowSendSuccess(false), 4000);
  };
  
  return (
    <div className="space-y-8 animate-fade-in max-w-[1440px] mx-auto pb-20">
        
        <div className="flex flex-col md:flex-row justify-between items-center md:items-end gap-6 text-center md:text-left px-4 sm:px-0">
            <div className="space-y-1">
                <h2 className="font-serif text-3xl sm:text-4xl text-[#0A0B0F] dark:text-white font-bold tracking-tight">My Wallet</h2>
                <p className="text-stone-500 text-sm">Manage your digital assets across the Ghost Network.</p>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
                <Button variant="outline" size="sm" className="flex-1 md:flex-none border-stone-200 dark:border-white/10 text-stone-700 dark:text-stone-300 rounded-sm" icon={<Settings size={14}/>} onClick={() => setShowSettings(true)}>Settings</Button>
            </div>
        </div>

        {/* 1. Portfolio Net Value */}
        <div className="px-4 sm:px-0">
            <Card className="bg-white dark:bg-[#0A0B0F] text-[#0A0B0F] dark:text-white border-stone-200 dark:border-white/10 relative overflow-hidden group shadow-xl p-8 sm:p-12 transition-colors duration-300 rounded-sm">
                <div className="relative z-10 flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                    <div className="space-y-4">
                        <div className="flex items-center justify-center lg:justify-start gap-3">
                            <div className="text-[10px] font-black uppercase tracking-[0.4em] text-stone-600 dark:text-stone-500">Total Balance</div>
                            <div className="px-2 py-0.5 rounded bg-stone-100 dark:bg-white/5 border border-stone-200 dark:border-white/10 text-[9px] font-mono text-stone-700 dark:text-stone-400 font-bold uppercase">Live</div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-end items-center sm:justify-start justify-center gap-4 sm:gap-6">
                            <div className="flex items-baseline gap-2">
                                <span className="text-4xl sm:text-6xl md:text-7xl lg:text-8xl font-serif font-medium tracking-tighter leading-none text-[#0A0B0F] dark:text-white">
                                    ${portfolioValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span className="text-stone-400 dark:text-stone-600 text-xl sm:text-2xl md:text-3xl font-serif">USD</span>
                            </div>
                            <div className="flex -space-x-3 mb-2 shrink-0">
                                {activeAssets.slice(0, 4).map(token => (
                                    <div key={token.mint} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white dark:border-[#0A0B0F] bg-stone-100 dark:bg-[#15171C] flex items-center justify-center overflow-hidden shadow-sm" title={token.symbol}>
                                        <TokenIcon symbol={token.symbol} fallback={token.icon} className="w-full h-full p-1.5" />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center justify-center lg:justify-start gap-4">
                            <div className={`flex items-center gap-1.5 text-xs sm:text-sm font-bold ${portfolioChangeClass} px-3 py-1 rounded-full bg-emerald-500/10 dark:bg-white/5 border border-emerald-500/20 dark:border-white/10 shadow-sm`}>
                                <TrendingUp size={16}/>
                                {portfolioChange.percent}%
                            </div>
                            <div className="text-[10px] sm:text-[11px] text-stone-600 dark:text-stone-400 font-bold tracking-wide font-mono">
                                {portfolioChange.isPositive ? '+' : '-'}{portfolioChange.value} <span className="opacity-60 uppercase ml-1 font-sans">24h</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap justify-center lg:justify-end gap-3">
                        <Button variant="outline" size="md" onClick={refreshBalances} className="border-stone-300 dark:border-white/10 text-stone-800 dark:text-white hover:bg-stone-50 dark:hover:bg-white/5 rounded-sm h-12 sm:h-14 px-6 sm:px-8 shadow-sm" icon={<RefreshCw size={18}/>}>Refresh</Button>
                        <Button variant="secondary" size="md" onClick={() => onNavigate('portfolio')} className="bg-[#0A0B0F] dark:bg-white text-white dark:text-black hover:opacity-90 rounded-sm h-12 sm:h-14 px-6 sm:px-8 shadow-lg" icon={<BarChart2 size={18}/>}>Portfolio</Button>
                    </div>
                </div>
            </Card>
        </div>

        {/* 2. Action Hub */}
        <div className="px-4 sm:px-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 sm:gap-6">
                {[
                    { label: 'Send', icon: ArrowUpRight, desc: 'Outbound', onClick: () => setShowSend(true), disabled: !isConnected, color: 'text-red-600 dark:text-red-500' },
                    { label: 'Receive', icon: ArrowDownLeft, desc: 'Deposit', onClick: () => setShowDeposit(true), disabled: !isConnected, color: 'text-emerald-600 dark:text-emerald-500' },
                    { label: 'Swap', icon: Repeat, desc: 'Exchange', onClick: () => onNavigate('swap'), disabled: !isConnected, color: 'text-indigo-600 dark:text-indigo-500' },
                    { label: 'Backup', icon: Key, desc: 'Seed', onClick: () => setShowSettings(true), disabled: !isConnected, color: 'text-amber-600 dark:text-amber-500' },
                    { label: 'Mixer', icon: Shield, desc: 'Private', onClick: () => setShowMixer(true), disabled: !isConnected, color: 'text-nobel-gold' },
                    { label: 'Settings', icon: Settings, desc: 'Config', onClick: () => setShowSettings(true), disabled: !isConnected, color: 'text-stone-600 dark:text-stone-400' }
                ].map((btn, i) => (
                    <button 
                        key={i}
                        onClick={btn.onClick}
                        disabled={btn.disabled}
                        className="group relative flex flex-col items-center justify-center p-6 bg-white dark:bg-[#15171C] rounded-sm border border-stone-200 dark:border-white/5 hover:border-nobel-gold/40 dark:hover:border-nobel-gold/40 transition-all duration-500 shadow-sm hover:shadow-2xl disabled:opacity-40 disabled:cursor-not-allowed h-36 md:h-40 overflow-hidden text-center"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-nobel-gold/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="w-12 h-12 rounded-sm flex items-center justify-center bg-stone-100 dark:bg-white/5 text-[#0A0B0F] dark:text-white group-hover:bg-nobel-gold group-hover:text-black transition-all duration-500 z-10 shadow-inner mb-3">
                            <btn.icon size={24} className={btn.color + " group-hover:text-black transition-colors"} />
                        </div>
                        <div className="relative z-10">
                            <span className="block font-black text-[10px] sm:text-xs text-[#0A0B0F] dark:text-white mb-0.5 uppercase tracking-widest">{btn.label}</span>
                            <span className="block text-[8px] uppercase font-black tracking-[0.2em] text-stone-600 dark:text-stone-400 opacity-60">{btn.desc}</span>
                        </div>
                    </button>
                ))}
            </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 px-4 sm:px-0">
            {/* 3. Key Holdings */}
            <div className="lg:col-span-5 h-full">
                <Card noPadding className="h-full overflow-hidden border-stone-200 dark:border-white/10 shadow-sm transition-colors rounded-sm">
                    <div className="p-6 sm:p-8 border-b border-stone-100 dark:border-white/5 flex flex-col sm:flex-row justify-between items-center bg-gray-50/50 dark:bg-white/[0.01] text-center sm:text-left gap-4">
                        <div>
                            <h3 className="font-serif text-xl sm:text-2xl font-bold text-stone-900 dark:text-white tracking-tight">My Assets</h3>
                            <p className="text-[10px] text-stone-600 dark:text-stone-400 uppercase font-bold tracking-[0.2em] mt-0.5">Current balances</p>
                        </div>
                        <span className="text-[10px] font-bold bg-stone-100 dark:bg-white/10 text-stone-700 dark:text-stone-300 px-4 py-1.5 rounded-full uppercase tracking-wider">{activeAssets.length} Assets</span>
                    </div>
                    <div className="divide-y divide-stone-100 dark:divide-white/5 overflow-y-auto custom-scrollbar max-h-[600px]">
                        {activeAssets.map(token => <AssetRow key={token.mint} token={token} />)}
                        {activeAssets.length === 0 && (
                            <div className="p-20 text-center text-stone-500 flex flex-col items-center">
                                <Zap size={32} className="opacity-20 mb-4" />
                                <p className="font-medium text-sm">No funded assets detected.</p>
                                <Button onClick={() => setShowDeposit(true)} variant="outline" size="sm" className="mt-4 rounded-sm border-stone-300">Deposit Funds</Button>
                            </div>
                        )}
                    </div>
                    <div className="p-4 border-t border-stone-100 dark:border-white/5">
                        <Button onClick={() => onNavigate('portfolio')} variant="ghost" className="w-full text-[10px] font-black uppercase tracking-widest text-stone-500 hover:text-nobel-gold">View Portfolio Analytics</Button>
                    </div>
                </Card>
            </div>

            {/* 4. Real-Time Transaction History (Integration Point) */}
            <div className="lg:col-span-7 h-full">
                <Card noPadding className="h-full flex flex-col border-stone-200 dark:border-white/10 overflow-hidden transition-colors rounded-sm min-h-[500px]">
                    <div className="p-6 sm:p-8 border-b border-stone-100 dark:border-white/5 flex flex-col sm:flex-row justify-between items-center bg-stone-50/30 dark:bg-white/[0.01] text-center sm:text-left gap-4">
                        <div>
                            <h3 className="font-serif text-xl sm:text-2xl font-bold text-[#0A0B0F] dark:text-white">Transaction History</h3>
                            <p className="text-[10px] text-stone-600 dark:text-stone-400 uppercase font-black tracking-[0.2em] mt-0.5">Recent activity</p>
                        </div>
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/5 border border-emerald-500/10 rounded-lg">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></div>
                            <span className="text-[9px] font-mono font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-widest">Connected</span>
                        </div>
                    </div>
                    
                    <div className="flex-1 overflow-y-auto custom-scrollbar p-2 sm:p-4">
                        {isConnected && walletAddress ? (
                            <WalletTransactionList walletAddress={walletAddress} limit={10} />
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center p-12 py-32 font-sans">
                                <div className="w-20 h-20 rounded bg-stone-100 dark:bg-white/5 flex items-center justify-center text-stone-400 mb-8 border border-stone-200 dark:border-white/10 shadow-inner">
                                    <ShieldCheck size={40} />
                                </div>
                                <h4 className="font-serif text-2xl font-bold text-stone-900 dark:text-white mb-3">Wallet Disconnected</h4>
                                <p className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed max-w-xs mx-auto mb-10">
                                    Connect your wallet to view your transaction history and account details.
                                </p>
                                <Button variant="secondary" size="lg" className="rounded-sm px-12 h-16 text-base bg-[#0A0B0F] text-white border-none shadow-xl" onClick={() => onNavigate('dashboard')}>Connect Wallet</Button>
                            </div>
                        )}
                    </div>

                    <div className="p-4 border-t border-stone-100 dark:border-white/5 bg-stone-50/50 dark:bg-black/20">
                         <Button 
                            variant="secondary" 
                            size="md" 
                            onClick={() => onNavigate('transaction-history')}
                            className="w-full h-14 rounded-sm bg-[#0A0B0F] dark:bg-white text-white dark:text-black font-black uppercase tracking-[0.2em] text-xs shadow-xl group"
                        >
                            View All Transactions <ChevronRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
        
        {/* Modals */}
        <AnimatePresence>
            {showDeposit && <DepositModal onClose={() => setShowDeposit(false)} address={walletAddress} balances={balances} />}
            {showSend && <SendModal onCancel={() => setShowSend(false)} onComplete={handleSendComplete} balances={balances} />}
            {showSettings && <WalletSettingsModal onClose={() => setShowSettings(false)} />}
            {showMixer && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
                    <Card className="max-w-md w-full p-10 text-center space-y-6 border-nobel-gold/20 bg-white dark:bg-[#15171C] transition-colors rounded-sm shadow-[0_30px_60px_-12px_rgba(0,0,0,0.5)]">
                        <div className="w-20 h-20 bg-nobel-gold/10 rounded-sm flex items-center justify-center text-nobel-gold mx-auto border border-nobel-gold/20 shadow-inner">
                            <Shield size={40} />
                        </div>
                        <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#0A0B0F] dark:text-white">Privacy Mixer</h3>
                        <p className="text-stone-600 dark:text-stone-400 text-sm leading-relaxed">
                            Zero-Knowledge anonymization is currently undergoing protocol stress testing. Enrollment is open to <span className="text-[#8A6D3B] dark:text-nobel-gold font-bold">Premium Tier</span> partners.
                        </p>
                        <div className="pt-4 border-t border-stone-100 dark:border-white/5 space-y-3">
                            <Button onClick={() => onNavigate('docs')} variant="outline" className="w-full h-12 rounded-sm text-stone-700 dark:text-stone-300 border-stone-200">View Documentation</Button>
                            <Button onClick={() => setShowMixer(false)} variant="secondary" className="w-full h-12 rounded-sm bg-[#0A0B0F] text-white border-none shadow-lg">Return to Wallet</Button>
                        </div>
                    </Card>
                </div>
            )}
        </AnimatePresence>
    </div>
  );
};
